import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Spin, Tag, Space, Button, Select, Input } from 'antd';
import { UserOutlined, FileOutlined, CloudDownloadOutlined, MailOutlined, DatabaseOutlined, FolderOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './AdminDashboard.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [fileGroups, setFileGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [userFilter, setUserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchStatistics();
    fetchUsers();
  }, [user, navigate]);

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/api/admin/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      const params = {};
      if (userFilter) params.userId = userFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await api.get('/api/admin/files', { params });
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  };

  const fetchFileGroups = async () => {
    try {
      const params = {};
      if (userFilter) params.userId = userFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/api/admin/file-groups', { params });
      setFileGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch file groups:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'files') {
      fetchFiles();
    } else if (activeTab === 'fileGroups') {
      fetchFileGroups();
    }
  }, [activeTab, userFilter, statusFilter, searchQuery]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/api/admin/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const userColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name || '-',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role, record) => (
        <Select
          value={role || 'user'}
          onChange={(value) => handleRoleChange(record.id, value)}
          style={{ width: 100 }}
        >
          <Option value="user">User</Option>
          <Option value="admin">Admin</Option>
        </Select>
      ),
    },
    {
      title: 'Files',
      dataIndex: 'filesCount',
      key: 'filesCount',
      render: (count) => <Tag>{count}</Tag>,
    },
    {
      title: 'Groups',
      dataIndex: 'fileGroupsCount',
      key: 'fileGroupsCount',
      render: (count) => <Tag>{count}</Tag>,
    },
    {
      title: 'Storage',
      dataIndex: 'storage',
      key: 'storage',
      render: (size) => formatFileSize(size),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const fileColumns = [
    {
      title: 'File Name',
      dataIndex: 'originalName',
      key: 'originalName',
      ellipsis: true,
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      render: (user) => user.email,
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size) => formatFileSize(size),
    },
    {
      title: 'Downloads',
      dataIndex: 'downloads',
      key: 'downloads',
      render: (count) => <Tag>{count}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'deletedAt',
      key: 'status',
      render: (deletedAt) => deletedAt ? 
        <Tag color="red">Deleted</Tag> : 
        <Tag color="green">Active</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.deletedAt && (
            <Button 
              danger 
              size="small"
              onClick={() => handleDeleteFile(record.id)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleDeleteFile = async (fileId) => {
    try {
      await api.delete(`/api/admin/files/${fileId}`);
      fetchFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const handleDeleteFileGroup = async (groupId) => {
    try {
      await api.delete(`/api/admin/file-groups/${groupId}`);
      fetchFileGroups();
    } catch (error) {
      console.error('Failed to delete file group:', error);
    }
  };

  return (
    <div className="admin-dashboard">
      <Title level={2}>Admin Dashboard</Title>
      
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            type={activeTab === 'overview' ? 'primary' : 'default'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </Button>
          <Button 
            type={activeTab === 'users' ? 'primary' : 'default'}
            onClick={() => setActiveTab('users')}
          >
            Users
          </Button>
          <Button 
            type={activeTab === 'files' ? 'primary' : 'default'}
            onClick={() => setActiveTab('files')}
          >
            Files
          </Button>
          <Button 
            type={activeTab === 'fileGroups' ? 'primary' : 'default'}
            onClick={() => setActiveTab('fileGroups')}
          >
            File Groups
          </Button>
        </Space>
      </div>

      {activeTab === 'overview' && statistics && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Users"
                  value={statistics.overview.totalUsers}
                  prefix={<UserOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Files"
                  value={statistics.overview.totalFiles}
                  prefix={<FileOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Downloads"
                  value={statistics.overview.totalDownloads}
                  prefix={<CloudDownloadOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Storage"
                  value={formatFileSize(statistics.overview.totalStorage)}
                  prefix={<DatabaseOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Active Files"
                  value={statistics.overview.activeFiles}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Deleted Files"
                  value={statistics.overview.deletedFiles}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="File Groups"
                  value={statistics.overview.totalFileGroups}
                  prefix={<FolderOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Emails Sent"
                  value={statistics.overview.totalEmailsSent}
                  prefix={<MailOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Card title="Top Uploaders">
                <Table
                  dataSource={statistics.topUploaders}
                  columns={[
                    { title: 'Email', dataIndex: 'email', key: 'email' },
                    { title: 'Files', dataIndex: 'filesCount', key: 'files' },
                    { title: 'Groups', dataIndex: 'fileGroupsCount', key: 'groups' },
                  ]}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Most Downloaded Files">
                <Table
                  dataSource={statistics.mostDownloaded}
                  columns={[
                    { title: 'File', dataIndex: 'name', key: 'name', ellipsis: true },
                    { title: 'Downloads', dataIndex: 'downloads', key: 'downloads' },
                    { title: 'Uploaded By', dataIndex: 'uploadedBy', key: 'uploadedBy', ellipsis: true },
                  ]}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {activeTab === 'users' && (
        <Card>
          <Table
            dataSource={users}
            columns={userColumns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      )}

      {activeTab === 'files' && (
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder="Filter by user"
              style={{ width: 200 }}
              allowClear
              onChange={setUserFilter}
            >
              {users.map(user => (
                <Option key={user.id} value={user.id}>{user.email}</Option>
              ))}
            </Select>
            <Select
              value={statusFilter}
              style={{ width: 120 }}
              onChange={setStatusFilter}
            >
              <Option value="all">All Files</Option>
              <Option value="active">Active</Option>
              <Option value="deleted">Deleted</Option>
            </Select>
            <Search
              placeholder="Search files"
              style={{ width: 200 }}
              onSearch={setSearchQuery}
            />
          </Space>
          <Table
            dataSource={files}
            columns={fileColumns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      )}

      {activeTab === 'fileGroups' && (
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder="Filter by user"
              style={{ width: 200 }}
              allowClear
              onChange={setUserFilter}
            >
              {users.map(user => (
                <Option key={user.id} value={user.id}>{user.email}</Option>
              ))}
            </Select>
            <Select
              value={statusFilter}
              style={{ width: 120 }}
              onChange={setStatusFilter}
            >
              <Option value="all">All Groups</Option>
              <Option value="active">Active</Option>
              <Option value="deleted">Deleted</Option>
            </Select>
          </Space>
          <Table
            dataSource={fileGroups}
            columns={[
              {
                title: 'Files Count',
                dataIndex: 'files',
                key: 'files',
                render: (files) => <Tag>{files.length} files</Tag>,
              },
              {
                title: 'Total Size',
                dataIndex: 'totalSize',
                key: 'totalSize',
                render: (size) => formatFileSize(size),
              },
              {
                title: 'Uploaded By',
                dataIndex: 'uploadedBy',
                key: 'uploadedBy',
                render: (user) => user.email,
              },
              {
                title: 'Downloads',
                dataIndex: 'downloads',
                key: 'downloads',
                render: (count) => <Tag>{count}</Tag>,
              },
              {
                title: 'Status',
                dataIndex: 'deletedAt',
                key: 'status',
                render: (deletedAt) => deletedAt ? 
                  <Tag color="red">Deleted</Tag> : 
                  <Tag color="green">Active</Tag>,
              },
              {
                title: 'Created',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => new Date(date).toLocaleDateString(),
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Space>
                    {!record.deletedAt && (
                      <Button 
                        danger 
                        size="small"
                        onClick={() => handleDeleteFileGroup(record.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Space>
                ),
              },
            ]}
            rowKey="id"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;